/* ============================================================
   cartColorView — MS-2 (TASK-004f86db) + MS-3 (TASK-bfa93913):
   pure view model for the print-color pick at cart checkout.

   MS-2: a color is a print attribute — the picker exists only while
   the cart has quote-print lines and the pick rides CheckoutDto.PrintColor
   onto every PerformerCart order (snapshot rules live server-side).

   MS-3: cart lines now carry their performer's stock palette
   (stockColors: null = not tracking -> standard palette; [] = tracking
   but sold out). Checkout still sends ONE color for ALL quote-print
   orders, so the offered palette is the intersection of every print
   line's contribution (case-insensitive, stock order preserved):
   - no print lines            -> empty palette (block hidden);
   - any line sold out, or no common color -> 'ask' (уточните в переписке);
   - >=1 stock-tracking line   -> 'stock' (В наличии у исполнителей);
   - only non-tracking lines   -> standard palette (MS-2 posture, unchanged).

   Pure functions; CartPage owns the state (MS-1 posture helpers
   resolveSelection/colorCaption are reused verbatim).
   ============================================================ */

import type { CartItemView } from '../types/product';
import { DEFAULT_COLOR_PALETTE, colorCaption, type ColorChoice } from './performerColorsView.ts';

/** Numeric CartItemKind.QuotePrint (defensive: number or numeric string). */
const QUOTE_PRINT_KIND = 1;

function isColor(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function printLines(items: readonly CartItemView[] | null | undefined): CartItemView[] {
  return (items ?? []).filter((i) => Number(i.kind ?? 0) === QUOTE_PRINT_KIND);
}

/** True when any cart line is a calculated print job. */
export function cartHasPrintJobs(items: readonly CartItemView[] | null | undefined): boolean {
  return printLines(items).length > 0;
}

/** True when some print line tracks stock but is sold out ([] palette). */
function anyLineSoldOut(lines: readonly CartItemView[]): boolean {
  return lines.some((l) => Array.isArray(l.stockColors) && l.stockColors.filter(isColor).length === 0);
}

/** Case-insensitive intersection; keeps the first list's order. */
function intersect(lists: readonly (readonly string[])[]): string[] {
  const [first, ...rest] = lists;
  return first.filter(
    (color) => !rest.some((list) => !list.some((c) => c.toLowerCase() === color.toLowerCase())),
  );
}

/**
 * Color picker posture for the cart checkout (MS-3 semantics, see header):
 * palette mode with the standard colors when no print performer tracks
 * stock, stock mode with the common in-stock colors otherwise, ask mode
 * when a color cannot be guaranteed (sold out / no common color).
 */
export function cartPrintColorChoice(items: readonly CartItemView[] | null | undefined): ColorChoice {
  const lines = printLines(items);
  if (lines.length === 0) return { mode: 'palette', colors: [] };

  let anyStock = false;
  const lists: string[][] = [];
  for (const line of lines) {
    if (line.stockColors != null) {
      const colors = line.stockColors.filter(isColor);
      if (colors.length === 0) return { mode: 'ask', colors: [] };
      anyStock = true;
      lists.push(colors);
    } else {
      lists.push([...DEFAULT_COLOR_PALETTE]);
    }
  }

  const colors = intersect(lists);
  if (colors.length === 0) return { mode: 'ask', colors: [] };
  return anyStock
    ? { mode: 'stock', colors }
    : { mode: 'palette', colors };
}

/**
 * ecfe2257 (Artem 2026-08-28 §5): color→hex lookup across every print
 * line's stockSwatches (union — the concrete palette intersects, but a hex
 * from any performer still renders the swatch for its own color label).
 * First valid hex per case-insensitive color wins.
 */
export function cartHexByColor(items: readonly CartItemView[] | null | undefined): Map<string, string> {
  const map = new Map<string, string>();
  for (const line of printLines(items)) {
    for (const swatch of line.stockSwatches ?? []) {
      if (typeof swatch?.color !== 'string') continue;
      const key = swatch.color.toLowerCase();
      if (map.has(key)) continue;
      const hex = typeof swatch.hexColor === 'string' ? swatch.hexColor.trim() : '';
      if (/^#[0-9A-Fa-f]{6}$/.test(hex)) map.set(key, hex.toUpperCase());
    }
  }
  return map;
}

/**
 * ecfe2257: «Многоцветная» and «Любой цвет» chips are offered whenever the
 * cart has print jobs — even in the ask posture (sold out / no common
 * color), because neither mode blocks on per-color stock: multicolor defers
 * slot mapping to comments/chat, any color is total-availability only
 * (backend MaterialStockRules.LimitsByTotalOnly).
 */
export function cartOffersModeChips(items: readonly CartItemView[] | null | undefined): boolean {
  return cartHasPrintJobs(items);
}

/**
 * Caption under the picker heading for the cart (plural performers — the
 * cart may span several of them; palette wording is the shared MS-1 text).
 */
export function cartColorCaption(
  choice: ColorChoice,
  items: readonly CartItemView[] | null | undefined,
): string {
  switch (choice.mode) {
    case 'stock':
      return 'В наличии у исполнителей';
    case 'ask':
      return anyLineSoldOut(printLines(items))
        ? 'Все цвета исполнителя распроданы — уточните цвет в переписке или выберите другого исполнителя'
        : 'У исполнителей нет общего цвета в наличии — уточните цвет в переписке';
    default:
      return colorCaption(choice);
  }
}
